import { useState } from "react";
import {
  AppBar,
  Alert,
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Button,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import DashboardRounded from "@mui/icons-material/DashboardRounded";
import AutoStoriesRounded from "@mui/icons-material/AutoStoriesRounded";
import SettingsRounded from "@mui/icons-material/SettingsRounded";
import AdminPanelSettingsRounded from "@mui/icons-material/AdminPanelSettingsRounded";
import PaymentsRounded from "@mui/icons-material/PaymentsRounded";
import MenuRounded from "@mui/icons-material/MenuRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import LightModeRounded from "@mui/icons-material/LightModeRounded";
import DarkModeRounded from "@mui/icons-material/DarkModeRounded";
import type { PlanEntitlement } from "@ebook/contracts";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { PlanUpgradeDialog } from "./PlanUpgradeDialog";
import { AddEbookDialog } from "./AddEbookDialog";
import { useTranslate } from "../i18n";
import { api } from "../lib/api-client";
import { planById } from "../lib/plans";
import { useAuthStore } from "../stores/auth-store";
import { useLocaleStore } from "../stores/locale-store";
import { useThemeStore } from "../stores/theme-store";

const drawerWidth = 248;
const navigation = [
  { label: "Dashboard", path: "/", icon: <DashboardRounded /> },
  { label: "Ebooks", path: "/ebooks", icon: <AutoStoriesRounded /> },
  { label: "Settings", path: "/settings", icon: <SettingsRounded /> },
] as const;
const adminNavigation = [
  { label: "Admin", path: "/admin/users", icon: <AdminPanelSettingsRounded /> },
  { label: "Payment", path: "/admin/payments", icon: <PaymentsRounded /> },
] as const;

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [savingLocale, setSavingLocale] = useState(false);
  const [localeError, setLocaleError] = useState("");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [entitlement, setEntitlement] = useState<PlanEntitlement | null>(null);
  const [entitlementError, setEntitlementError] = useState("");
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { user, signOut } = useAuthStore();
  const { locale, setLocale } = useLocaleStore();
  const { mode, toggleMode } = useThemeStore();
  const __ = useTranslate();
  const currentPlan = planById(user?.plan);

  async function toggleLocale() {
    if (savingLocale) return;
    const nextLocale = locale === "th" ? "en" : "th";
    setSavingLocale(true);
    setLocaleError("");
    try {
      const settings = await api.settings();
      const updated = await api.updateSettings({
        locale: nextLocale,
        theme: settings.theme,
        openAiModel: settings.openAiModel,
      });
      setLocale(updated.locale);
    } catch (error) {
      setLocaleError(error instanceof Error ? error.message : __("เปลี่ยนภาษาไม่สำเร็จ"));
    } finally {
      setSavingLocale(false);
    }
  }

  async function openAddEbook() {
    setEntitlementError("");
    try {
      const quota = await api.entitlement();
      setEntitlement(quota);
      setAddOpen(true);
    } catch (error) {
      setEntitlementError(error instanceof Error ? error.message : __("โหลดสิทธิ์การใช้งานไม่สำเร็จ"));
    }
  }

  const drawer = <Box>
    <Toolbar sx={{ px: 3 }}><Typography variant="h6" fontWeight={800} color="primary">{__("Ebook Studio")}</Typography></Toolbar>
    <Divider />
    <Box sx={{ px: 1.5, pt: 1.5 }}>
      <Button
        fullWidth
        variant="contained"
        startIcon={<AddRounded />}
        onClick={() => { setMobileOpen(false); void openAddEbook(); }}
      >
        {__("เพิ่มอีบุ๊ก")}
      </Button>
    </Box>
    <List sx={{ p: 1.5 }}>
      {[...navigation, ...(user?.role === "admin" ? adminNavigation : [])].map((item) => <ListItemButton
        key={item.path}
        selected={item.path === "/" ? pathname === "/" : pathname.startsWith(item.path)}
        onClick={() => { void navigate({ to: item.path }); setMobileOpen(false); }}
        sx={{ borderRadius: 2, mb: 0.5 }}
      >
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText primary={__(item.label)} />
      </ListItemButton>)}
    </List>
  </Box>;

  return <Box sx={{ display: "flex", minHeight: "100vh" }}>
    <AppBar position="fixed" color="inherit" elevation={0} sx={{ borderBottom: 1, borderColor: "divider", ml: { md: `${drawerWidth}px` }, width: { md: `calc(100% - ${drawerWidth}px)` } }}>
      <Toolbar>
        <IconButton onClick={() => setMobileOpen(true)} sx={{ display: { md: "none" }, mr: 1 }} aria-label={__("เปิดเมนู")}><MenuRounded /></IconButton>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={toggleMode} sx={{ mr: 1 }} aria-label={mode === "dark" ? __("สลับเป็นโหมดสว่าง") : __("สลับเป็นโหมดมืด")}>
          {mode === "dark" ? <LightModeRounded /> : <DarkModeRounded />}
        </IconButton>
        <Tooltip title={locale === "th" ? __("เปลี่ยนภาษาเป็น English") : __("เปลี่ยนภาษาเป็นไทย")}>
          <span>
            <IconButton
              disabled={savingLocale}
              onClick={() => void toggleLocale()}
              sx={{ mr: 1, fontSize: 22 }}
              aria-label={locale === "th" ? __("เปลี่ยนภาษาเป็น English") : __("เปลี่ยนภาษาเป็นไทย")}
            >
              {locale === "th" ? "🇹🇭" : "🇺🇸"}
            </IconButton>
          </span>
        </Tooltip>
        <Chip
          label={currentPlan ? currentPlan.name : __("เลือกแผน")}
          size="small"
          color="primary"
          variant="outlined"
          onClick={() => setUpgradeOpen(true)}
          sx={{ mr: 1.5, fontWeight: 700 }}
        />
        <IconButton onClick={(event) => setAnchor(event.currentTarget)} aria-label={__("เมนูโปรไฟล์")}>
          <Avatar src={user?.photoUrl ?? undefined} sx={{ width: 36, height: 36 }}>{user?.displayName.charAt(0)}</Avatar>
        </IconButton>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
          <MenuItem onClick={() => { setAnchor(null); void navigate({ to: "/profile" }); }}>{__("View profile")}</MenuItem>
          <MenuItem onClick={() => { setAnchor(null); void signOut().then(() => navigate({ to: "/login" })); }}>{__("Sign out")}</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
    <Snackbar
      open={Boolean(localeError || entitlementError)}
      autoHideDuration={4000}
      onClose={() => { setLocaleError(""); setEntitlementError(""); }}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        severity="error"
        variant="filled"
        onClose={() => { setLocaleError(""); setEntitlementError(""); }}
      >
        {localeError || entitlementError}
      </Alert>
    </Snackbar>
    <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{ display: { xs: "block", md: "none" }, "& .MuiDrawer-paper": { width: drawerWidth } }}>{drawer}</Drawer>
      <Drawer variant="permanent" open sx={{ display: { xs: "none", md: "block" }, "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box" } }}>{drawer}</Drawer>
    </Box>
    <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${drawerWidth}px)` } }}>
      <Toolbar />
      <Box sx={{ p: { xs: 2, sm: 3, lg: 4 } }}>
        <Outlet />
      </Box>
    </Box>
    <PlanUpgradeDialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    <AddEbookDialog
      open={addOpen}
      pageLimit={entitlement?.pagesPerBook ?? 3000}
      onClose={() => setAddOpen(false)}
      onCreated={(ebookId) => {
        setAddOpen(false);
        void navigate({ to: "/ebooks/$ebookId", params: { ebookId }, search: { tab: "general" } });
      }}
    />
  </Box>;
}
